/**
 * useSystemPublic Hook
 * Fetches public system information WITHOUT authentication
 *
 * Purpose: Use on Login page and other unauthenticated pages to fetch branding info
 * Endpoint: GET /api/system/public/info (no auth required)
 *
 * Returns:
 * - name: System name (e.g., "SmartDoc")
 * - description: System description
 * - branding: Logo, colors, company name
 * - features: Public feature flags (chat_enabled, faq_enabled, etc.)
 */

import { log } from '@/utils/errors/logger';
import { QUERY_STALE_TIME } from '@/constants/queryConfig';
import { createQueryHookNoParams } from '@/hooks/api/factories';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Public system information (no authentication required)
 * Subset of Organization with only public fields
 */
export interface PublicSystemInfo {
  name: string;
  description?: string;
  branding?: {
    primary_color?: string;
    secondary_color?: string;
    logo_url?: string | null;
    company_name?: string | null;
    login_background_url?: string | null;
    login_icon_url?: string | null;
  };
  features?: {
    chat_enabled?: boolean;
    faq_enabled?: boolean;
    document_upload?: boolean;
  };
}

// ============================================================================
// Query Keys
// ============================================================================

export const systemPublicQueryKeys = {
  all: ['system', 'public'] as const,
  info: () => [...systemPublicQueryKeys.all, 'info'] as const,
};

// ============================================================================
// Query Hook
// ============================================================================

/**
 * Hook to get public system information WITHOUT authentication.
 * Safe to use on Login page and other unauthenticated pages.
 *
 * Uses VERY_LONG stale time (30 minutes) as system info rarely changes.
 *
 * Usage:
 * @example
 * const { data: system, isLoading } = useGetSystemPublic();
 * console.log(system?.name); // "SmartDoc"
 * console.log(system?.branding?.primary_color); // "#1976d2"
 */
export const useGetSystemPublic = createQueryHookNoParams<PublicSystemInfo>({
  queryKey: () => systemPublicQueryKeys.info(),
  queryFn: (apiClient) =>
    apiClient.getPublicOrganizationInfoApiSystemPublicInfoGet().then(response => {
      log.api.response('GET', '/api/system/public/info', 200, {
        name: response.data.name,
      });
      return response.data as PublicSystemInfo;
    }),
  logContext: 'GET /api/system/public/info',
  staleTime: QUERY_STALE_TIME.VERY_LONG, // 30 minutes - system info rarely changes
});

/**
 * Convenience hook that provides public system info with a simple API.
 * Use this on unauthenticated pages (Login, Register, etc.)
 *
 * Returns:
 * - system: PublicSystemInfo data or null
 * - isLoading: Loading state
 * - error: Error if fetch failed
 * - refetch: Manual refetch function
 *
 * Usage:
 * @example
 * const { system, isLoading } = useSystemPublic();
 *
 * if (isLoading) return <Loading />;
 * if (!system) return <Error />;
 *
 * console.log(system.name);
 * console.log(system.branding?.primary_color);
 */
export const useSystemPublic = () => {
  const { data, isLoading, error, refetch } = useGetSystemPublic();

  return {
    system: data ?? null,
    isLoading,
    error,
    refetch,
  };
};
